import { FormattedMessage } from 'react-intl';
import HomePage from '@/pages/HomePage/HomePage';
import ResourcesPage from '@/pages/Resources/ResourcesPage';
import GamesResourcesPage from '@/pages/Resources/GamesResourcesPage';
import ComplianceDocumentsResourcesPage from '@/pages/Resources/ComplianceDocumentsResourcesPage';
import MarketingAssetsResourcesPage from '@/pages/Resources/MarketingAssetsResourcesPage';
import PartnersAdminPage from '@/pages/PartnersAdminPage/PartnersAdminPage';
import PartnerAdminPage from '@/pages/PartnerAdminPage/PartnerAdminPage';
import GamesAdminPage from '@/pages/GamesAdminPage/GamesAdminPage';
import GameAdminPage from '@/pages/GameAdminPage/GameAdminPage';
import MarketsAdminPage from '@/pages/MarketsAdminPage/MarketsAdminPage';
import MarketAdminPage from '@/pages/MarketAdminPage/MarketAdminPage';
import GamePage from '@/pages/GamePage/GamePage';
import LoginPage from '@/pages/LoginPage';
import UnauthorizedPage from '@/pages/UnauthorizedPage/UnauthorizedPage';
import EmailNotVerifiedPage from '@/pages/EmailNotVerifiedPage/EmailNotVerifiedPage';
import NotFoundPage from '@/pages/NotFoundPage/NotFoundPage';
import SharedBasketPage from '@/pages/SharedBasketPage/SharedBasketPage';
import AuditLogAdminPage from '@/pages/AuditLogAdminPage/AuditLogAdminPage';
import ComplianceCoverageAdminPage from '@/pages/ComplianceCoverageAdminPage/ComplianceCoverageAdminPage';
import { UserRole } from './types';

export type RouteConfig = {
  index?: boolean;
  key?: string;
  path: string;
  page?: React.ReactNode;
  requireLogin?: boolean;
  secure?: boolean;
  ariaLabel?: string;
  menuLabel?: React.ReactNode;
  routes?: RouteConfig[];
  allowedRoles?: UserRole[];
  requiredPermissions?: string[];
  showNavigation?: boolean;
};

const routes: RouteConfig[] = [
  {
    index: true,
    path: '/',
    page: <HomePage />,
    requireLogin: true,
    ariaLabel: 'Home',
    menuLabel: <FormattedMessage id="home" defaultMessage="Home" />,
    requiredPermissions: ['games.read'],
  },
  {
    key: 'resourcesMainRoute',
    path: '',
    page: <ResourcesPage />,
    requireLogin: true,
    ariaLabel: 'Resources menu',
    menuLabel: (
      <FormattedMessage id="resources" defaultMessage="Resources" />
    ),
    routes: [
      {
        path: '/games',
        page: <GamesResourcesPage />,
        ariaLabel: 'Games',
        menuLabel: (
          <FormattedMessage id="games" defaultMessage="Games" />
        ),
        showNavigation: false,
        requiredPermissions: ['markets.read', 'games.read'],
      },
      {
        path: '/compliance',
        page: <ComplianceDocumentsResourcesPage />,
        ariaLabel: 'Compliance Documents',
        menuLabel: (
          <FormattedMessage
            id="complianceDocuments"
            defaultMessage="Compliance Documents"
          />
        ),
        requiredPermissions: [
          'markets.read',
          'compliance_documents.read',
        ],
        showNavigation: false,
      },
      {
        path: 'marketing',
        page: <MarketingAssetsResourcesPage />,
        ariaLabel: 'Marketing Assets',
        menuLabel: (
          <FormattedMessage
            id="marketingAssets"
            defaultMessage="Marketing Assets"
          />
        ),
        requiredPermissions: [
          'markets.read',
          'marketing_assets.read',
        ],
        showNavigation: false,
      },
    ],
  },
  {
    path: '/admin',
    ariaLabel: 'Admin',
    menuLabel: <FormattedMessage id="admin" defaultMessage="Admin" />,
    routes: [
      {
        path: 'partners',
        page: <PartnersAdminPage />,
        requiredPermissions: [
          'partners.read',
          'users.create',
          'users.read',
          'users.update',
          'users.delete',
          'users.manage_roles',
          'users.manage_permissions',
        ],
        ariaLabel: 'Partners',
        menuLabel: (
          <FormattedMessage id="partners" defaultMessage="Partners" />
        ),
      },
      {
        path: 'partners/:partnerId',
        page: <PartnerAdminPage />,
        ariaLabel: 'Partner',
        requiredPermissions: [
          'partners.read',
          'users.create',
          'users.read',
          'users.update',
          'users.delete',
          'users.manage_roles',
          'users.manage_permissions',
        ],
      },
      {
        path: 'games',
        page: <GamesAdminPage />,
        ariaLabel: 'Games',
        requiredPermissions: [
          'markets.read',
          'games.read',
          'compliance_documents.update',
          'marketing_assets.update',
        ],
        menuLabel: (
          <FormattedMessage id="games" defaultMessage="Games" />
        ),
      },
      {
        path: 'games/:gameId',
        page: <GameAdminPage />,
        ariaLabel: 'Game',
        requiredPermissions: [
          'markets.read',
          'games.read',
          'compliance_documents.update',
          'marketing_assets.update',
        ],
      },
      {
        path: 'markets',
        page: <MarketsAdminPage />,
        ariaLabel: 'Markets',
        requiredPermissions: [
          'markets.read',
          'compliance_documents.update',
          'marketing_assets.update',
        ],
        menuLabel: (
          <FormattedMessage id="markets" defaultMessage="Markets" />
        ),
      },
      {
        path: 'markets/:marketId',
        page: <MarketAdminPage />,
        ariaLabel: 'Market',
        requiredPermissions: [
          'markets.read',
          'compliance_documents.update',
          'marketing_assets.update',
        ],
      },
      {
        path: 'compliance-coverage',
        page: <ComplianceCoverageAdminPage />,
        ariaLabel: 'Compliance Coverage',
        // The Games admin permissions, plus what the coverage API
        // checks: an internal role and compliance_documents.read.
        requiredPermissions: [
          'markets.read',
          'games.read',
          'compliance_documents.read',
          'compliance_documents.update',
          'marketing_assets.update',
        ],
        allowedRoles: [
          'Super Admin',
          'Internal Admin',
          'Internal Viewer',
        ],
        menuLabel: (
          <FormattedMessage
            id="complianceCoverage"
            defaultMessage="Compliance Coverage"
          />
        ),
      },
      {
        path: 'audit-log',
        page: <AuditLogAdminPage />,
        ariaLabel: 'Activity Log',
        menuLabel: (
          <FormattedMessage
            id="activityLog"
            defaultMessage="Activity Log"
          />
        ),
        allowedRoles: ['Super Admin'],
      },
    ],
  },
  {
    path: '/game/:studioGameId',
    page: <GamePage />,
    secure: true,
    requiredPermissions: ['markets.read', 'games.read'],
    showNavigation: true,
  },
  {
    path: '/login',
    page: <LoginPage />,
    showNavigation: false,
  },
  {
    path: '/unauthorized',
    page: <UnauthorizedPage />,
    showNavigation: false,
  },
  {
    path: '/email-not-verified',
    page: <EmailNotVerifiedPage />,
    showNavigation: false,
  },
  {
    path: '/not-found',
    page: <NotFoundPage />,
    showNavigation: false,
  },
  {
    path: '/shared/:token',
    page: <SharedBasketPage />,
    requireLogin: true,
    showNavigation: true,
  },
  {
    path: '*',
    page: <HomePage />,
    secure: true,
  },
];

export default routes;
